import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { SeedAccount, SeedTestRun, SeedTestTriggerResponse } from '@/lib/types-deliverability'

const SEED_REFETCH_MS = 30_000

export function useSeedAccounts() {
  return useQuery<{ data: SeedAccount[] }>({
    queryKey: ['seeds'],
    queryFn: () => apiClient.get(endpoints.seeds.list),
    refetchInterval: SEED_REFETCH_MS,
  })
}

export function useSeedResults(page: number) {
  return useQuery<{ data: SeedTestRun[]; total: number; page: number; per_page: number }>({
    queryKey: ['seeds', 'results', page],
    queryFn: () => apiClient.get(endpoints.seeds.results({ page })),
    refetchInterval: SEED_REFETCH_MS,
  })
}

export function useRunSeedTest() {
  const queryClient = useQueryClient()
  return useMutation<SeedTestTriggerResponse>({
    mutationFn: () => apiClient.post(endpoints.seeds.test),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seeds'] })
    },
  })
}

export function useAddSeed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; provider: string }) =>
      apiClient.post(endpoints.seeds.list, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seeds'] })
    },
  })
}

export function useDeleteSeed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.del(endpoints.seeds.delete(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seeds'] })
    },
  })
}
